import * as React from 'react'
import {
  Routes as RouterRoutes,
  Route,
  Navigate,
  Outlet,
} from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import Invite from 'components/Invite'
import Layout from 'components/Layout'
import Logout from 'components/Logout'
import AddGuestInfo from '../components/AddGuestInfo/AddGuestInfo'
import ErrorFallback from 'views/ErrorFallback/ErrorFallback'
import Login from 'views/Login/Login'
import MobileLogin from 'views/Login/MobileLogin'
import Procedures from 'views/Procedures'
import Procedure from 'views/Procedure/Procedure'
import Profile from 'views/Profile'
import StatusCheck from 'views/Status/StatusCheck'
import InventoryReport from 'views/InventoryReport/InventoryReport'
import Receive from 'views/ReceiveProducts/Receive'
import GuestAccess from 'views/GuestAccess/GuestAccess'

import { useUser } from 'app/User'
import { BottomNavigationProvider } from 'lib/context/BottomNavigation'
import ProtectedUserRoute from 'app/ProtectedUserRoute'
import { DashboardLayout } from 'components/Dashboard/components/DashboardLayout'
import { UndocumentedAssetsProvider } from 'lib/context/UndocumentedAssetsContext'
import CaptureTray from 'views/DigitalTrayMapping/CaptureTray/CaptureTray'

import {
  ASSET_REPORTS,
  INVENTORY_REPORT,
  LOCATION_MANAGEMENT,
  RECEIVE,
  PATIENT_REPORT,
  ANALYTICS,
  PRODUCT_EMAIL_SERVICE,
  CATALOG,
  REFERRING_PHYSICIANS,
  LABOR_COST,
  SCRUB_TECHNICIANS,
  PATIENT_WALLET_CARD,
} from './ROUTES_CONSTANTS'
import InventoryProvider from 'lib/apollo/inventory-config'
import InternalManagement from 'views/InternalManagement/InternalManagement'
import InternalManagementLayout from 'components/InternalManagementLayout/InternalManagementLayout'
import DTMAnalysisResults from 'views/InternalManagement/DTMAnalysisResults/DTMAnalysisResults'
import SPD from 'views/SPDLayout/SPD/SPD'
import { SPDProvider } from 'views/SPDLayout/SPD/SPD.context'
import LocationManagement from 'views/LocationManagement/LocationManagement'
import { TrayManagementProvider } from 'views/TrayManagementLayout/TrayManagement/TrayManagement.context'
import PreferenceCards from 'views/PreferenceCards/PreferenceCards'
import CardView from 'views/PreferenceCards/CardView/CardView'
import PatientReportsPage from 'views/PatientReport/PatientReport'
import { ProcedureDetailsReport } from 'views/PatientReport/ProcedureDetailsReport/ProcedureDetailsReport'
import AssetsReports from 'views/AssetsReports/AssetsReports'
import { SPDScanProvider } from 'views/SPDLayout/SPD/Scan/Scan.context'
import { SPDCortexScannerProvider } from 'views/SPDLayout/SPD/SPDCortexScanner/SPDCortexScanner.context'
import { CaptureTrayContextProvider } from 'views/DigitalTrayMapping/CaptureTray/CaptureTray.context'
import { AssignedDigitalTraysProvider } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.context'
import SPDLayout from 'views/SPDLayout/SPDLayout'
import TrayManagementLayout from 'views/TrayManagementLayout/TrayManagementLayout'
import { InventoryReportProvider } from 'views/InventoryReport/InventoryReportProvider'
import { AssetTypeProvider } from 'lib/context/AssetTypeContext/AssetTypeContext'
import AddProcedure from 'views/AddProcedure/AddProcedure'
import Analytics from 'views/Analytics/Analytics'
import ProductEmailService from 'views/ProductEmailService/ProductEmailService'
import Catalog from 'views/Catalog/Catalog'
import ReferringPhysicians from 'views/ReferringPhysicians/ReferringPhysicians'
import NewReferringPhysician from 'views/ReferringPhysicians/NewReferringPhysician/NewReferringPhysician'
import LaborCost from 'views/LaborCost/LaborCost'
import ScrubTechnicians from 'views/ScrubTechnicians/ScrubTechnicians'
import NewScrubTechnician from 'views/ScrubTechnicians/NewScrubTechnician/NewScrubTechnician'
import { TrayConfigurationProvider as TrayConfigurationProviderSPD } from 'views/TrayConfiguration/TrayConfiguration.context'
import SpecialRequestResponse from 'views/SpecialRequestResponse/SpecialRequestResponse'
import PatientWalletCard from 'views/PatientWalletCard/PatientWalletCard'
import RoomView from 'views/RoomView/RoomView'

declare global {
  interface Window {
    __inviteFlowStarted: boolean
  }
}

window.__inviteFlowStarted = false

function Routes() {
  const [accountChecked, setAccountChecked] = React.useState(false)
  const navigate = useNavigate()
  const { user, isAuthenticated } = useUser()

  const isInternalUser = user?.roles?.includes('MGAT_INTERNAL')
  const isSPDUser = user?.roles?.includes('MGAT_SPD')
  const isGuestEmail = user?.email === 'guest@medgeo.com'
  const pathToNavigateTo = isGuestEmail
    ? 'guest-info'
    : isInternalUser
    ? 'internal'
    : isSPDUser
    ? 'tray-management'
    : 'surgeries'

  React.useEffect(() => {
    setAccountChecked(true)
  }, [accountChecked, navigate, user])

  if (!accountChecked) {
    return null
  }

  return (
    <RouterRoutes>
      <Route
        path="/special-request/:token"
        element={<SpecialRequestResponse />}
      />
      <Route
        path="/"
        element={isAuthenticated ? <Navigate to="/home" replace /> : <Login />}
      />
      <Route path="/login" element={<MobileLogin />} />
      <Route path="/guest-access" element={<GuestAccess />} />
      <Route
        path="invite/:inviteCode"
        element={
          <ProtectedUserRoute roles={['MGAT_REP']}>
            <Layout>
              <Invite />
            </Layout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="logout"
        element={
          <Layout>
            <Logout />
          </Layout>
        }
      />
      <Route
        path="home"
        element={<Navigate to={`/${pathToNavigateTo}`} replace />}
      />
      <Route
        path="surgeries"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSE', 'MGAT_REP']} allowGuest>
            <Layout>
              <Procedures />
            </Layout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="rooms"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSE', 'MGAT_REP']} allowGuest>
            <Layout newLayout>
              <RoomView />
            </Layout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="new-procedure"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSE', 'MGAT_NURSEADMIN']}>
            <Layout>
              <AddProcedure />
            </Layout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path={PATIENT_REPORT}
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <PatientReportsPage />
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path={`${PATIENT_REPORT}/:surgeryId/*`}
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color={'grayscale.lightest'}>
              <ProcedureDetailsReport />
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path={ASSET_REPORTS}
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <AssetsReports />
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="spd/*"
        element={
          <ProtectedUserRoute roles={['MGAT_SPD']}>
            <AssignedDigitalTraysProvider>
              <CaptureTrayContextProvider>
                <TrayConfigurationProviderSPD>
                  <TrayManagementProvider>
                    <SPDCortexScannerProvider>
                      <SPDProvider>
                        <SPDLayout>
                          <SPD />
                        </SPDLayout>
                      </SPDProvider>
                    </SPDCortexScannerProvider>
                  </TrayManagementProvider>
                </TrayConfigurationProviderSPD>
              </CaptureTrayContextProvider>
            </AssignedDigitalTraysProvider>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="tray-management/*"
        element={
          <InventoryProvider>
            <CaptureTrayContextProvider>
              <SPDProvider>
                <SPDScanProvider>
                  <SPDCortexScannerProvider>
                    <InventoryReportProvider>
                      <TrayManagementProvider>
                        <AssignedDigitalTraysProvider>
                          <TrayManagementLayout />
                        </AssignedDigitalTraysProvider>
                      </TrayManagementProvider>
                    </InventoryReportProvider>
                  </SPDCortexScannerProvider>
                </SPDScanProvider>
              </SPDProvider>
            </CaptureTrayContextProvider>
          </InventoryProvider>
        }
      />
      <Route
        path="/guest-info"
        element={
          <Layout>
            <AddGuestInfo />
          </Layout>
        }
      />
      <Route
        path="internal"
        element={
          <ProtectedUserRoute roles={['MGAT_INTERNAL']}>
            <InventoryProvider>
              <InternalManagementLayout />
            </InventoryProvider>
          </ProtectedUserRoute>
        }
      >
        <Route index element={<InternalManagement />} />
        <Route path="dtm-results" element={<DTMAnalysisResults />} />
      </Route>
      <Route
        path="surgeries/:surgeryId/*"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSE', 'MGAT_REP']} allowGuest>
            <Layout>
              <AssignedDigitalTraysProvider>
                <SPDProvider>
                  <SPDScanProvider>
                    <AssetTypeProvider>
                      <UndocumentedAssetsProvider>
                        <BottomNavigationProvider>
                          <Procedure />
                        </BottomNavigationProvider>
                      </UndocumentedAssetsProvider>
                    </AssetTypeProvider>
                  </SPDScanProvider>
                </SPDProvider>
              </AssignedDigitalTraysProvider>
            </Layout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="profile"
        element={
          <ProtectedUserRoute
            roles={['MGAT_NURSE', 'MGAT_NURSEADMIN', 'MGAT_REP', 'MGAT_SPD']}
            allowGuest
          >
            <Layout>
              <Profile />
            </Layout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="capture-tray/:trayID"
        element={
          <ProtectedUserRoute
            roles={['MGAT_REP', 'MGAT_NURSE', 'MGAT_NURSEADMIN']}
          >
            <Layout>
              <CaptureTray />
            </Layout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path={INVENTORY_REPORT}
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <InventoryProvider>
                <InventoryReport />
              </InventoryProvider>
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="preference-cards"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <PreferenceCards />
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="preference-cards/new"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <CardView mode="create" />
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="preference-card/:id"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <CardView mode="view" />
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path="preference-card/:id/clone"
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <CardView mode="clone" />
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route
        path={LOCATION_MANAGEMENT}
        element={
          <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
            <DashboardLayout color="white">
              <InventoryProvider>
                <LocationManagement />
              </InventoryProvider>
            </DashboardLayout>
          </ProtectedUserRoute>
        }
      />
      <Route path={'/'} element={<DashboardLayout color="white" />}>
        <Route path={`${RECEIVE}/*`} element={<Outlet />}>
          <Route
            index
            element={
              <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
                <InventoryProvider>
                  <Receive />
                </InventoryProvider>
              </ProtectedUserRoute>
            }
          />
        </Route>
        <Route
          path={ANALYTICS}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <Analytics />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={PRODUCT_EMAIL_SERVICE}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <ProductEmailService />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={CATALOG}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <Catalog />
            </ProtectedUserRoute>
          }
        />
      </Route>
      <Route path={'/'} element={<DashboardLayout color="white" />}>
        <Route path={`${RECEIVE}/*`} element={<Outlet />}>
          <Route
            index
            element={
              <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
                <InventoryProvider>
                  <Receive />
                </InventoryProvider>
              </ProtectedUserRoute>
            }
          />
        </Route>
        <Route
          path={REFERRING_PHYSICIANS}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <ReferringPhysicians />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={`${REFERRING_PHYSICIANS}/new`}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <NewReferringPhysician />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={`${REFERRING_PHYSICIANS}/:id`}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <NewReferringPhysician />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={SCRUB_TECHNICIANS}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <ScrubTechnicians />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={`${SCRUB_TECHNICIANS}/new`}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <NewScrubTechnician />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={`${SCRUB_TECHNICIANS}/:id`}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <NewScrubTechnician />
            </ProtectedUserRoute>
          }
        />
        <Route
          path={LABOR_COST}
          element={
            <ProtectedUserRoute roles={['MGAT_NURSEADMIN']}>
              <LaborCost />
            </ProtectedUserRoute>
          }
        />
      </Route>
      <Route path="status" element={<StatusCheck />} />
      <Route path="*" element={<ErrorFallback />} />
      <Route path={`/${PATIENT_WALLET_CARD}`} element={<PatientWalletCard />} />
    </RouterRoutes>
  )
}

export default Routes
